import { useCallback, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Check, X } from "lucide-react";
import { toast } from "sonner";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { queryClient } from "App";
import BaseTableV3 from "components/BaseTableV3/BaseTableV3";
import { getNextQueryParams } from "components/helpers";
import usePageTitle from "hooks/usePageTitle";
import { FEATURE_FLAG_ACCESS_TYPE } from "constants/Constants";
import { formatError } from "helpers/formatError";
import FeatureFlagService from "services/FeatureFlag";
import UserService from "services/User";
import { FeatureFlagsTableActions } from "routes/feature_flags/FeatureFlagsTableActions";

const FLAGS_QUERY_KEY = ["feature-flags"];

const ACCESS_TYPE_LABEL = {
  [FEATURE_FLAG_ACCESS_TYPE.EVERYONE]: "Everyone",
  [FEATURE_FLAG_ACCESS_TYPE.CUSTOM]: "Custom",
};

const BoolCell = ({ value, label }) =>
  value ? (
    <Check className="tw:size-4 tw:text-[#24B388]" aria-label={label} />
  ) : (
    <X
      className="tw:size-4 tw:text-muted-foreground"
      aria-label={`Not ${label}`}
    />
  );

const buildColumns = ({ canManage, onEdit, onDelete }) => [
  {
    header: "ID",
    id: "ID",
    accessorKey: "id",
    size: 50,
    cell: ({ row }) => (
      <p style={{ fontWeight: "bold" }}>{row.getValue("ID")}</p>
    ),
  },
  { id: "Key", accessorKey: "key", header: "Key", size: 200 },
  { id: "Name", accessorKey: "name", header: "Name", size: 200 },
  {
    id: "Enabled",
    accessorKey: "enabled",
    header: "Enabled",
    size: 90,
    enableSorting: false,
    cell: ({ row }) => (
      <BoolCell value={row.getValue("Enabled")} label="Enabled" />
    ),
  },
  {
    id: "Access Type",
    accessorKey: "access_type",
    header: "Access Type",
    size: 120,
    enableSorting: false,
    cell: ({ row }) => ACCESS_TYPE_LABEL[row.getValue("Access Type")] ?? "",
  },
  {
    id: "System",
    accessorKey: "is_system",
    header: "System",
    size: 90,
    enableSorting: false,
    cell: ({ row }) => (
      <BoolCell value={row.getValue("System")} label="System" />
    ),
  },
  {
    header: null,
    id: "Actions",
    accessorKey: "actions",
    size: 60,
    enableSorting: false,
    enableResizing: false,
    meta: { isDisplay: true },
    cell: ({ row }) => (
      <FeatureFlagsTableActions
        flag={row.original}
        canManage={canManage}
        onEdit={onEdit}
        onDelete={onDelete}
      />
    ),
  },
];

const FeatureFlagsPage = () => {
  const navigate = useNavigate();
  const canManage = UserService.isAdmin();
  const [deleteTarget, setDeleteTarget] = useState(null);

  usePageTitle("Feature Flags");

  const invalidateFlags = useCallback(
    () => queryClient.invalidateQueries({ queryKey: FLAGS_QUERY_KEY }),
    []
  );

  const handleEdit = useCallback(
    flag => navigate(`/settings/feature_flags/${flag.id}/edit`),
    [navigate]
  );

  // System flags cannot be deleted, so never open the confirm dialog for them.
  const handleDeleteRequest = useCallback(flag => {
    if (flag.is_system) return;
    setDeleteTarget(flag);
  }, []);

  const columns = useMemo(
    () =>
      buildColumns({
        canManage,
        onEdit: handleEdit,
        onDelete: handleDeleteRequest,
      }),
    [canManage, handleEdit, handleDeleteRequest]
  );

  const fetchFlags = useCallback(
    async queryParams => {
      const next = getNextQueryParams(queryParams, columns);
      return FeatureFlagService.list(next);
    },
    [columns]
  );

  const handleDelete = useCallback(async () => {
    try {
      await FeatureFlagService.delete(deleteTarget.id);
      toast.success("Flag deleted");
      setDeleteTarget(null);
      invalidateFlags();
    } catch (error) {
      toast.error(formatError(error, "Could not delete flag"));
    }
  }, [deleteTarget, invalidateFlags]);

  return (
    <div className="tw:flex tw:flex-col tw:flex-1 tw:min-h-0 tw:min-w-0">
      <BaseTableV3
        columns={columns}
        header="Feature Flags"
        tableName="feature_flags"
        queryKey={FLAGS_QUERY_KEY}
        fetchData={fetchFlags}
        invalidateData={invalidateFlags}
        enableSearch
        enableSettings
        createButton={
          canManage ? (
            <Button
              size="sm"
              onClick={() => navigate("/settings/feature_flags/new")}
            >
              Create flag
            </Button>
          ) : null
        }
      />
      <Dialog
        open={!!deleteTarget}
        onOpenChange={open => !open && setDeleteTarget(null)}
      >
        <DialogContent className="tw:sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>Delete flag</DialogTitle>
            <DialogDescription>
              Delete {deleteTarget?.name}? Consumers gating on{" "}
              <code>{deleteTarget?.key}</code> will no longer see it.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              type="button"
              variant="ghost"
              onClick={() => setDeleteTarget(null)}
            >
              Cancel
            </Button>
            <Button type="button" variant="destructive" onClick={handleDelete}>
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default FeatureFlagsPage;
